import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PanelTop, X, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { useScrollProgress } from '../hooks/useScrollProgress';
import ShareSheet from './ShareSheet';
import { getStorySharePayload, tryNativeShare } from '../utils/shareUtils';
import { useLanguage } from '../context/LanguageContext';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusableElements(container) {
  if (!container) return [];

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
  );
}

const navigationCopy = {
  en: {
    slideNames: [
      'Introduction', 'Thesis', 'Timeline', 'The Crack', 'Hxaro',
      'Gift Economies', 'Before & After', 'Why', 'Collaboration',
      'Data Deep Dive', 'Collaboration Scan', 'Simulator', 'Modern', 'Future', 'Sources', 'Join'
    ],
    sharedSuccessfully: 'Shared successfully.',
    chapterFallback: 'Chapter',
    previousSlide: 'Previous slide',
    nextSlide: 'Next slide',
    shareTitle: 'Share',
    shareThisPage: 'Share this page',
    closeChapterMenu: 'Close chapter menu',
    openChapterMenu: 'Open chapter menu',
    slideChapters: 'Slide chapters',
    goToPrefix: 'Go to',
    closeChapterMenuBackdrop: 'Close chapter menu backdrop',
    chapterNavigation: 'Chapter navigation',
    chapters: 'Chapters',
    keyboardHint: '← → Navigate slides',
    backToTop: 'Back to top',
    language: 'Language',
  },
  de: {
    slideNames: [
      'Einleitung', 'These', 'Zeitlinie', 'Der Riss', 'Hxaro',
      'Geschenkoekonomien', 'Vorher & Nachher', 'Warum', 'Kollaboration',
      'Daten-Deep-Dive', 'Kollaborations-Scan', 'Simulator', 'Moderne', 'Zukunft', 'Quellen', 'Mitmachen',
    ],
    sharedSuccessfully: 'Erfolgreich geteilt.',
    chapterFallback: 'Kapitel',
    previousSlide: 'Vorherige Folie',
    nextSlide: 'Naechste Folie',
    shareTitle: 'Teilen',
    shareThisPage: 'Diese Seite teilen',
    closeChapterMenu: 'Kapitelmenue schliessen',
    openChapterMenu: 'Kapitelmenue oeffnen',
    slideChapters: 'Folienkapitel',
    goToPrefix: 'Gehe zu',
    closeChapterMenuBackdrop: 'Kapitelmenue-Hintergrund schliessen',
    chapterNavigation: 'Kapitelnavigation',
    chapters: 'Kapitel',
    keyboardHint: '← → Folien navigieren',
    backToTop: 'Nach oben',
    language: 'Sprache',
  },
  es: {
    slideNames: [
      'Introducción', 'Tesis', 'Linea de tiempo', 'La grieta', 'Hxaro',
      'Economias del don', 'Antes y despues', 'Por que', 'Colaboracion',
      'Analisis de datos', 'Escaneo de colaboracion', 'Simulador', 'Moderno', 'Futuro', 'Fuentes', 'Unirse',
    ],
    sharedSuccessfully: 'Compartido correctamente.',
    chapterFallback: 'Capitulo',
    previousSlide: 'Diapositiva anterior',
    nextSlide: 'Siguiente diapositiva',
    shareTitle: 'Compartir',
    shareThisPage: 'Compartir esta pagina',
    closeChapterMenu: 'Cerrar menu de capitulos',
    openChapterMenu: 'Abrir menu de capitulos',
    slideChapters: 'Capitulos',
    goToPrefix: 'Ir a',
    closeChapterMenuBackdrop: 'Cerrar fondo del menu de capitulos',
    chapterNavigation: 'Navegacion por capitulos',
    chapters: 'Capitulos',
    keyboardHint: '← → Navegar diapositivas',
    backToTop: 'Volver arriba',
    language: 'Idioma',
  },
};

export default function Navigation({ currentSlide, goTo, total }) {
  const { locale } = useLanguage();
  const copy = navigationCopy[locale] || navigationCopy.en;
  const slideNames = copy.slideNames;
  const progress = useScrollProgress();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const shareTimeoutRef = useRef(null);
  const closeMenuButtonRef = useRef(null);
  const menuDialogRef = useRef(null);
  const menuLastFocusedRef = useRef(null);

  useEffect(() => {
    return () => {
      if (shareTimeoutRef.current) {
        clearTimeout(shareTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let timeout;
    let rafId = null;

    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    const showWithAutoHide = () => {
      if (mobileMedia.matches || !hoverMedia.matches) {
        setShowNav(true);
        return;
      }
      setShowNav(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowNav(false), 2500);
    };

    const scheduleShow = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        showWithAutoHide();
      });
    };

    const onScroll = () => {
      scheduleShow();
    };
    const onMove = () => {
      scheduleShow();
    };
    const onTouch = () => {
      scheduleShow();
    };
    const onKey = () => {
      scheduleShow();
    };

    showWithAutoHide();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('keydown', onKey);
      clearTimeout(timeout);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    menuLastFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeMenuButtonRef.current?.focus();

    const onDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(menuDialogRef.current);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const activeInsideDialog = menuDialogRef.current?.contains(active);

      if (!activeInsideDialog) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onDialogKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onDialogKeyDown);
      menuLastFocusedRef.current?.focus?.();
      menuLastFocusedRef.current = null;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mobileMedia = window.matchMedia('(max-width: 767px)');

    const closeOnMobile = (event) => {
      if (event.matches) {
        setMenuOpen(false);
      }
    };

    closeOnMobile(mobileMedia);

    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', closeOnMobile);
      return () => {
        mobileMedia.removeEventListener('change', closeOnMobile);
      };
    }

    mobileMedia.addListener(closeOnMobile);
    return () => {
      mobileMedia.removeListener(closeOnMobile);
    };
  }, []);

  const showShareStatus = (message) => {
    setShareMessage(message);
    if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    shareTimeoutRef.current = window.setTimeout(() => {
      setShareMessage('');
    }, 2200);
  };

  const buildSharePayload = () => getStorySharePayload(locale);

  const handleShare = async () => {
    try {
      const shared = await tryNativeShare(buildSharePayload());

      if (shared) {
        showShareStatus(copy.sharedSuccessfully);
        return;
      }

      setShareSheetOpen(true);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      setShareSheetOpen(true);
    }
  };

  const currentChapter = slideNames[currentSlide] || copy.chapterFallback;

  return (
    <>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-[#cfdcf6]/70 dark:bg-[#1a243f]/80">
        <motion.div
          className="h-full bg-gradient-to-r from-[#6f89be] via-[#9cb4e7] to-[#bf8a55]"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* Floating nav */}
      <AnimatePresence>
        {showNav && (
          <motion.nav
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="hidden md:flex fixed top-3 right-4 z-40 items-center gap-1.5 backdrop-blur-xl bg-[#f8fbff]/84 dark:bg-[#1c2843]/82 rounded-2xl shadow-[0_18px_40px_-26px_rgba(14,24,45,0.75)] border border-[#c5d5f5]/70 dark:border-[#546a98]/40 px-2.5 py-2 text-[#1a243f] dark:text-[#dbe5ff]"
          >
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => goTo(currentSlide - 1)}
                disabled={currentSlide === 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#314972] dark:text-[#dbe5ff] hover:bg-[#e0eafe] dark:hover:bg-[#263556] disabled:opacity-35 disabled:cursor-not-allowed transition"
                aria-label={copy.previousSlide}
              >
                <ChevronLeft size={18} />
              </button>

              <span className="inline-flex min-w-[76px] items-center justify-center rounded-full border border-[#c7d7f4]/80 dark:border-[#4f6898]/55 bg-[#eaf1ff]/80 dark:bg-[#233351]/78 px-2.5 py-1 text-sm font-semibold text-[#213055] dark:text-[#edf3ff]">
                {currentSlide + 1} / {total}
              </span>

              <button
                onClick={() => goTo(currentSlide + 1)}
                disabled={currentSlide === total - 1}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#314972] dark:text-[#dbe5ff] hover:bg-[#e0eafe] dark:hover:bg-[#263556] disabled:opacity-35 disabled:cursor-not-allowed transition"
                aria-label={copy.nextSlide}
              >
                <ChevronRight size={18} />
              </button>

              <div className="h-6 w-px bg-[#9fb1d8]/75 dark:bg-[#5f77a7]/65" />

              <span className="max-w-[11rem] truncate text-[11px] font-semibold uppercase tracking-[0.09em] text-[#4f628d] dark:text-[#bec8e0]">
                {currentChapter}
              </span>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#314972] dark:text-[#dbe5ff] hover:bg-[#e0eafe] dark:hover:bg-[#263556] transition"
                aria-label={menuOpen ? copy.closeChapterMenu : copy.openChapterMenu}
                aria-controls="mobile-chapter-menu"
                aria-expanded={menuOpen}
              >
                {menuOpen ? <X size={18} /> : <PanelTop size={18} />}
              </button>

              <button
                onClick={handleShare}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#314972] dark:text-[#dbe5ff] hover:bg-[#e0eafe] dark:hover:bg-[#263556] transition"
                title={copy.shareTitle}
                aria-label={copy.shareThisPage}
              >
                <Send size={18} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <ShareSheet
        open={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        payload={buildSharePayload()}
        onStatus={showShareStatus}
      />

      <AnimatePresence>
        {shareMessage && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            aria-live="polite"
            className="fixed top-16 left-1/2 -translate-x-1/2 z-[72] text-[11px] md:text-xs px-3 py-1.5 rounded-full bg-[#f4f8ff]/96 dark:bg-[#1c2844]/96 border border-[#c3d3f2]/80 dark:border-[#526b99]/55 text-[#3d5280] dark:text-[#e3ecff] shadow-lg"
          >
            {shareMessage}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Chapter menu */}
      <AnimatePresence>
        {menuOpen && (
          <React.Fragment key="mobile-menu">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[55] bg-[#0e1528]/46 backdrop-blur-[1.5px]"
              onClick={() => setMenuOpen(false)}
              aria-label={copy.closeChapterMenuBackdrop}
            />
            <motion.div
              id="mobile-chapter-menu"
              ref={menuDialogRef}
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.985 }}
              className="fixed right-4 top-16 z-[60] w-80 max-h-[calc(100vh-5.5rem)] backdrop-blur-xl bg-[#f8fbff]/97 dark:bg-[#141d33]/96 shadow-2xl p-5 overflow-y-auto border border-[#c5d5f5]/70 dark:border-[#4f6696]/35 rounded-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-chapter-menu-title"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 id="mobile-chapter-menu-title" className="font-bold text-lg text-[#1e2f54] dark:text-[#edf3ff]">{copy.chapters}</h3>
                <button
                  ref={closeMenuButtonRef}
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#294678] dark:text-[#dbe7ff] hover:bg-[#e6efff] dark:hover:bg-[#21304f] transition"
                  aria-label={copy.closeChapterMenu}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-1">
                {slideNames.map((name, i) => (
                  <button
                    key={i}
                    onClick={() => { goTo(i); setMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition ${
                      i === currentSlide
                        ? 'bg-[#243658] text-[#f4e4cb] font-medium'
                        : 'hover:bg-[#e3edff] dark:hover:bg-[#21304f] text-[#314972] dark:text-[#c8d2ea]'
                    }`}
                    aria-current={i === currentSlide ? 'step' : undefined}
                  >
                    <span className="text-xs text-[#7f93bb] mr-2">{String(i + 1).padStart(2, '0')}</span>
                    {name}
                  </button>
                ))}
              </div>
            </motion.div>
          </React.Fragment>
        )}
      </AnimatePresence>

      {/* Keyboard hint — desktop only */}
      <div className="fixed bottom-4 right-4 z-30 hidden md:block">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 3 }}
          className="text-xs text-[#4f628d] dark:text-[#bec8e0] bg-[#f4f8ff]/70 dark:bg-[#1a2541]/70 backdrop-blur rounded-lg px-3 py-1.5 border border-[#c4d3f3]/60 dark:border-[#556c9b]/35"
        >
          {copy.keyboardHint}
        </motion.div>
      </div>
    </>
  );
}
